import { Hidden, useMediaQuery } from '@mui/material';
import { Author, AuthorImage } from 'domain/shared/author';
import React, { lazy, useState, useEffect } from 'react';
import { Prompt } from 'react-router-dom';
import { CommunityUpdateVisibility, UpdateVisibility } from 'types/content/update';
import Button from 'ui/elements/buttons/Button';
import { DocumentList } from 'types/documents';
import Loading from 'ui/elements/Loading';
import Dropzone from 'domain/shared/Files/Upload/Dropzone';
import AttachmentsCarousel from 'domain/Updates/shared/UpdateForm/AttachmentsCarousel';
import Dialog, { Title, Content } from 'ui/views/dialogs/Dialog';
import { bluePlanetTheme } from 'ui/theme';
import { communityMentionSearchFunction, removeMentions } from 'ui/elements/form/RichTextEditor/utils';
import { communityUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import { useUpdateForm } from 'domain/Updates/shared/UpdateForm/useUpdateForm';
import { CommunityConfiguration, TinyCommunity } from 'types/company/community';
import { CompanyUpdateFormValues } from 'domain/Updates/company/types';
import CommunityTopicList from './CommunityTopicList';
import { PostedBy } from './utils';
import { VisibilitySelector } from '../shared/Visibility/VisibilitySelector';
import VisibilityButton from '../shared/Visibility/VisibilityButton';
import { getVisibilityLabel } from '../shared/Visibility/utils';
import { PostTo } from '../shared/types';
import { notEmpty } from 'util/arrayUtils';
import { KpiEmbed } from 'domain/kpis/types';
import { companiesApi } from 'apis/CompanyAPI/companies/companiesApi';
import i18n from 'util/i18n';

const RichTextEditor = lazy(() => import('ui/elements/form/RichTextEditor'));

interface Props {
  titleDraft?: string;
  contentDraft?: string;
  bannerImage?: string;
  community: TinyCommunity;
  communityConfiguration: CommunityConfiguration;
  postedBy: PostedBy;
  onSave: (dto: CompanyUpdateFormValues, visibility: UpdateVisibility) => void;
  isSaving: boolean;
  author: Author;
  onClose: () => void;
  selectedVisibility: CommunityUpdateVisibility;
  attachments?: DocumentList;
  linkPreview?: string;
  saveButtonText?: string;
  contentId?: string;
  initialCursorPosition?: number;
}

export default function UpdateForm(props: Props) {
  const { community, communityConfiguration, postedBy } = props;
  const [isDialogMounted, setIsDialogMounted] = useState(false);

  // Track that dialog has been mounted to prevent prompt on initial render
  useEffect(() => {
    setIsDialogMounted(true);
  }, []);

  const isPostingAsCommunity = postedBy.type === 'community';

  const getImageUploadUrl = (filename: string) => companiesApi.images.getCompanyUploadUrl(props.community.id, filename);
  const api = communityUpdateAPI(props.community.id);

  const [isVisiblitiySelectorOpen, setIsVisibilitySelectorOpen] = useState(false);
  const [visibility, setVisibility] = useState(props.selectedVisibility);
  const [pendingKpiEmbed, setPendingKpiEmbed] = useState<KpiEmbed | undefined>();
  const postTo: PostTo = {
    type: 'community',
    profile: community,
    visibility,
  };

  const { formik, newAttachments, onAttachmentRemoved, onUploadFile, removeFile, onCancel, onSubmit, hasRealChanges } = useUpdateForm({
    ...props,
    onSave: dto => props.onSave(dto, visibility),
  });

  const isMobile = useMediaQuery(bluePlanetTheme.breakpoints.down('sm'));

  const mentionSearchFunction = async (searchQuery: string) =>
    communityMentionSearchFunction(searchQuery, visibility, props.community.id);
  const onClose = () => onCancel(props.onClose);

  const onChangeVisibility = (visibility: CommunityUpdateVisibility) => {
    setVisibility(visibility);
    // when we change visibility, we don't know if the mentioned companies/users can still see the update, so we remove them
    formik.setFieldValue('structuredContent', removeMentions(formik.values.structuredContent));
  };

  const visibilityOptions: CommunityUpdateVisibility[] = [
    'CommunityMembers' as const,
    isPostingAsCommunity && communityConfiguration.companyMembersIsEnabled ? ('Founders' as const) : undefined,
    isPostingAsCommunity && communityConfiguration.companyMembersIsEnabled
      ? ('CommunityMembersExceptFounders' as const)
      : undefined,
  ].filter(notEmpty);

  return (
    <>
      <Prompt when={isDialogMounted && hasRealChanges && !formik.isSubmitting} message={i18n('en').common.unsavedChangesPrompt} />
      <Dialog
        maxWidth={isVisiblitiySelectorOpen ? undefined : 'md'}
        fullScreen={isMobile}
        marginTop="15vh"
        open
        onClose={onClose}
      >
        {isVisiblitiySelectorOpen ? (
        <VisibilitySelector
          value={postTo}
          options={visibilityOptions.map(visibility => ({
            ...postTo,
            visibility,
          }))}
          onChange={option => (option.type === 'community' ? onChangeVisibility(option.visibility) : undefined)}
          onClose={() => setIsVisibilitySelectorOpen(false)}
        />
      ) : (
        <>
          <Title onClose={onClose}>
            <div className="u-flex u-flex-space-between u-flex-align-center" style={{ width: '100%' }}>
              <div className="u-flex u-flex-align-center">
                <AuthorImage className="u-half-spacing-right" author={props.author} />
                <VisibilityButton
                  label={getVisibilityLabel(postTo, true)}
                  onClick={() => setIsVisibilitySelectorOpen(true)}
                />
              </div>
              <Hidden smDown>
                <Button
                  onClick={() => onSubmit()}
                  kind="primary"
                  type="submit"
                  data-intercom-target="publish"
                  isLoading={props.isSaving}
                  className="u-half-spacing-right"
                >
                  {props.saveButtonText || 'Publish'}
                </Button>
              </Hidden>
            </div>
          </Title>
          <Content className="u-section-spacing-bottom">
            {props.communityConfiguration.updateTopicsIsEnabled && !props.contentId && (
              <CommunityTopicList
                communityId={props.community.id}
                onChange={topicId => formik.setFieldValue('topicId', topicId)}
              />
            )}

            <React.Suspense fallback={<Loading spacing="content-spacing" position="center" />}>
              <Dropzone disableOnClick onDrop={onUploadFile} tabIndex={-1}>
                <RichTextEditor
                  toolbarType="none"
                  name="content"
                  deltaName="structuredContent"
                  content={formik.values.content}
                  delta={formik.values.structuredContent || {}}
                  linkPreview={props.linkPreview}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  validateField={formik.validateField}
                  handleChange={formik.handleChange}
                  error={formik.touched.content ? formik.errors.content : undefined}
                  getImageUploadUrl={getImageUploadUrl}
                  mentionSearchFunction={mentionSearchFunction}
                  onUploadFile={onUploadFile}
                  kpiOptions={
                    postedBy.type === 'company' && postedBy.company
                      ? {
                          company: postedBy.company,
                          pendingKpiEmbed,
                          onKpiEmbedInserted: () => setPendingKpiEmbed(undefined),
                        }
                      : undefined
                  }
                  submitButton={
                    <Button
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => onSubmit()}
                      kind="primary"
                      type="submit"
                      data-intercom-target="publish"
                      isLoading={props.isSaving}
                    >
                      {props.saveButtonText || 'Publish'}
                    </Button>
                  }
                  attachmentSection={
                    <AttachmentsCarousel
                      attachmentIdList={formik.values.attachments}
                      existingAttachments={props.attachments?.documents ?? []}
                      newAttachments={newAttachments}
                      downloadAttachment={(updateId: string) =>
                        api.attachments.download(props.contentId || '', updateId)
                      }
                      removeExistingAttachment={onAttachmentRemoved}
                      removeNewAttachment={removeFile}
                    />
                  }
                  autoFocus
                  initialCursorPosition={props.initialCursorPosition}
                />
              </Dropzone>
            </React.Suspense>
          </Content>
        </>
      )}
      </Dialog>
    </>
  );
}
